// @boty/database - Creator Message Query Utilities (Story 4.11)

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, CreatorMessageInsert } from '../types';

type DbClient = SupabaseClient<Database>;

/**
 * Insert a new creator message.
 */
export async function insertCreatorMessage(
  client: DbClient,
  data: CreatorMessageInsert,
) {
  const { data: result, error } = await client
    .from('creator_messages')
    .insert(data)
    .select()
    .single();

  if (error) throw error;
  return result;
}

/**
 * Get all creator messages for a bot (for creator dashboard).
 */
export async function getCreatorMessagesByBotId(
  client: DbClient,
  botId: string,
) {
  const { data, error } = await client
    .from('creator_messages')
    .select('*')
    .eq('bot_id', botId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Count creator messages for a session+bot (for rate limiting).
 */
export async function countCreatorMessagesBySession(
  client: DbClient,
  botId: string,
  sessionId: string,
) {
  const { count, error } = await client
    .from('creator_messages')
    .select('*', { count: 'exact', head: true })
    .eq('bot_id', botId)
    .eq('session_id', sessionId);

  if (error) throw error;
  return count ?? 0;
}

/**
 * Mark a creator message as notified (update notified_at timestamp).
 */
export async function markCreatorMessageNotified(
  client: DbClient,
  messageId: string,
) {
  const { error } = await client
    .from('creator_messages')
    .update({ notified_at: new Date().toISOString() })
    .eq('id', messageId);

  if (error) throw error;
}
